/**
 * 「模型查询」子页（design model-management-revamp §6.2、§10）。
 *
 * 上方筛选（模型类别 / 版本号 / 上传人 / 上传时间 / 状态 / 来源），下方模型列表：
 * 模型类别、一级分类、二级分类、仿真模块、框架、安装目录、更新时间、版本数量。
 * 打开页面时一次取回所有类别的版本，在前端筛选；有版本筛选时只显示有命中的类别并自动展开，
 * 展开后只列命中的版本（版本号关键字高亮）。每个版本再展开为小模型列表：模型名称 + tag（文件后缀）。
 * 未登记的名称在列表里标「未登记」，管理员可按平台建议登记；管理员可编辑已登记名称。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, DatePicker, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import {
  createModelName,
  getModelCategories,
  getModelDetail,
  listModelNames,
  listModels,
  listUnregisteredModelNames,
  patchModelName,
  setModelVersionActive,
  type ModelCategories,
  type ModelNamePatch,
  type UnregisteredModelName,
} from '@/apis/model';
import { useCurrentUser } from '@/common/auth/adminAcl';
import SubModelTable, { SubModelCount } from './SubModelTable';
import './ModelQuery.less';
import {
  RUNTIME_MODULE_LABEL,
  buildVersionRows,
  categoryLabel,
  typeLabelText,
  ndpTypeLabel,
  filterVersions,
  hasVersionFilter,
  mergeModelRows,
  splitHighlight,
  unregisteredConflicts,
  type MergedModelRow,
  type NameReg,
  type VersionRow,
  type VersionSource,
  type VersionFilter,
  type VersionStatusFilter,
} from '@/utils/modelCatalog';

const { Text } = Typography;

function errText(e: unknown): string {
  const any = e as { response?: { data?: { message?: string; detail?: unknown } }; message?: string };
  const d = any?.response?.data;
  if (d?.message) {
    return d.message;
  }
  if (d?.detail) {
    return typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail);
  }
  return any?.message ?? String(e);
}

const RUNTIME_EMPTY_HINT = '仿真不会安装此目录的模型';

function fmtTime(iso: string | null | undefined): string {
  return iso ? iso.replace('T', ' ').slice(0, 16) : '—';
}

const SOURCE_LABEL: Record<VersionSource, string> = { upload: '手工上传', ndp: '中台同步' };

// ============================================================
// 主页面：筛选 + 可展开列表
// ============================================================

const ModelQuery: React.FC = () => {
  const { hasPerm } = useCurrentUser();
  // 安装目录、分类只允许管理员修改（design §11.3，与系统配置同权限点）
  const isAdmin = hasPerm('menu:sys-config');

  const [cats, setCats] = useState<ModelCategories | null>(null);
  const [rows, setRows] = useState<MergedModelRow[]>([]);
  const [versions, setVersions] = useState<Record<string, VersionRow[]>>({});
  const [unreg, setUnreg] = useState<Record<string, UnregisteredModelName>>({});
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [uploader, setUploader] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [status, setStatus] = useState<VersionStatusFilter>('active');
  const [source, setSource] = useState<VersionSource | ''>('');
  const [expanded, setExpanded] = useState<string[]>([]);

  const [registerTarget, setRegisterTarget] = useState<UnregisteredModelName | null>(null);
  const [editTarget, setEditTarget] = useState<NameReg | null>(null);

  useEffect(() => {
    getModelCategories()
      .then(setCats)
      .catch((e) => message.error(`加载模型分类失败：${errText(e)}`));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [r, names, un] = await Promise.all([
          listModels({ include_all_inactive: true }),
          listModelNames({ include_disabled: true }),
          listUnregisteredModelNames(),
        ]);
        const merged = mergeModelRows(r.items, names);
        // 各类别版本分别取：某个类别失败只影响它自己，列表照常显示
        const withVersions = merged.filter((m) => m.total_versions > 0);
        const details = await Promise.allSettled(withVersions.map((m) => getModelDetail(m.name)));
        if (cancelled) {
          return;
        }
        const next: Record<string, VersionRow[]> = {};
        details.forEach((d, i) => {
          const n = withVersions[i].name;
          if (d.status === 'fulfilled') {
            next[n] = d.value ? buildVersionRows(n, d.value.history) : [];
          } else {
            message.error(`加载 ${n} 的版本失败：${errText(d.reason)}`);
          }
        });
        setRows(merged);
        setUnreg(Object.fromEntries(un.map((u) => [u.name, u])));
        setVersions(next);
      } catch (e) {
        message.error(`加载模型列表失败：${errText(e)}`);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const uploaderOptions = useMemo(
    () => Array.from(new Set(Object.values(versions).flat().map((v) => v.uploader).filter((u) => u !== '—')))
      .sort().map((u) => ({ label: u, value: u })),
    [versions],
  );

  // 筛选栏只改草稿，点「查询」（或在版本号框回车）才生效；类别卡片点击即生效
  const [vf, setVf] = useState<VersionFilter>({ status: 'active' });
  // 版本号框的回车 / 清空会带上最新值（此时 version 状态可能还没更新）
  const search = (kw: string = version) => setVf({
    version: kw, uploader, status, source,
    from: range?.[0]?.format('YYYY-MM-DD'), to: range?.[1]?.format('YYYY-MM-DD'),
  });
  const versionFiltering = hasVersionFilter(vf);

  const hits = useMemo(
    () => Object.fromEntries(Object.entries(versions).map(([n, vs]) => [n, filterVersions(vs, vf)])),
    [versions, vf],
  );

  const filtered = useMemo(
    () => rows.filter((r) => (!name || r.name === name) && (!versionFiltering || (hits[r.name]?.length ?? 0) > 0)),
    [rows, name, versionFiltering, hits],
  );
  const hitCount = filtered.reduce((n, r) => n + (hits[r.name]?.length ?? 0), 0);

  // 筛选条件变化时：有版本筛选则展开命中的类别，否则收起。
  // 只跟筛选条件走、不跟数据走——停用 / 启用后重新加载时保留用户当前展开的行。
  const filterKey = JSON.stringify([name, vf]);
  useEffect(() => {
    setExpanded(versionFiltering ? filtered.map((r) => r.name) : []);
  }, [filterKey]);

  const resetFilters = () => {
    setName('');
    setVersion('');
    setUploader(undefined);
    setRange(null);
    setStatus('active');
    setSource('');
    setVf({ status: 'active' });
  };

  const columns: ColumnsType<MergedModelRow> = [
    {
      title: '模型类别', dataIndex: 'name', width: 240, fixed: 'left',
      render: (n: string, r) => (
        <Space size={6} wrap={false}>
          <Text strong style={{ fontFamily: 'ui-monospace, monospace' }} ellipsis={{ tooltip: n }}>{n}</Text>
          {!r.reg && <Tag color="orange" style={{ marginInlineEnd: 0 }}>未登记</Tag>}
          {r.reg?.status === 'disabled' && <Tag style={{ marginInlineEnd: 0 }}>停用</Tag>}
          {r.all_inactive && <Tag style={{ marginInlineEnd: 0 }}>版本全停用</Tag>}
        </Space>
      ),
    },
    { title: '一级分类', width: 100, render: (_, r) => categoryLabel(cats, r.category) },
    { title: '二级分类', dataIndex: 'type', width: 150, render: (v: string | null) => v ?? '—' },
    {
      // 与中台页面上的模型类型名一致（模型分类配置里维护的二级分类中文名）
      title: '中台模型类别', dataIndex: 'type', key: 'ndp_type', width: 160,
      render: (v: string | null) => ndpTypeLabel(cats, v) ?? <Text type="secondary">—</Text>,
    },
    {
      title: '仿真模块', width: 110,
      render: (_, r) => {
        if (!r.reg) {
          return <Text type="secondary">—</Text>;
        }
        const m = r.reg.runtime_module;
        return m ? RUNTIME_MODULE_LABEL[m] ?? m : (
          <Tooltip title={RUNTIME_EMPTY_HINT}><Text type="secondary">不安装</Text></Tooltip>
        );
      },
    },
    {
      title: '框架', dataIndex: 'frameworks', width: 130,
      render: (fs: string[]) => (fs.length ? fs.map((f) => <Tag key={f}>{f}</Tag>) : <Text type="secondary">—</Text>),
    },
    {
      title: '安装目录', width: 320,
      render: (_, r) => (r.reg
        ? <Text copyable={{ text: r.reg.install_dir }} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.reg.install_dir}</Text>
        : <Text type="secondary">—</Text>),
    },
    {
      title: '更新时间', dataIndex: 'last_uploaded', width: 150,
      sorter: (a, b) => (a.last_uploaded ?? '').localeCompare(b.last_uploaded ?? ''),
      render: (v: string | null) => <Text style={{ fontSize: 12 }}>{fmtTime(v)}</Text>,
    },
    {
      title: '版本数量', width: 100, align: 'right',
      render: (_, r) => (
        <Tooltip title={`可用 ${r.active_versions} / 共 ${r.total_versions}`}>
          <span>{r.active_versions}<Text type="secondary"> / {r.total_versions}</Text></span>
        </Tooltip>
      ),
    },
    {
      title: '操作', width: 90, fixed: 'right',
      render: (_, r) => {
        if (!r.reg) {
          const u = unreg[r.name];
          return (
            <Tooltip title={!isAdmin ? '登记需要「系统参数运维」权限' : !u ? '该模型类别没有可用版本，无需登记' : undefined}>
              <Button size="small" type="link" disabled={!isAdmin || !u} onClick={() => u && setRegisterTarget(u)}>登记</Button>
            </Tooltip>
          );
        }
        return isAdmin ? <Button size="small" type="link" onClick={() => setEditTarget(r.reg)}>编辑</Button> : null;
      },
    },
  ];

  const totalActive = rows.reduce((n, r) => n + r.active_versions, 0);
  const totalAll = rows.reduce((n, r) => n + r.total_versions, 0);

  return (
    <div className="mq">
      <div className="mq-cards">
        <button type="button" className={`mq-card${name === '' ? ' is-active' : ''}`} onClick={() => setName('')}>
          <div className="mq-card-head"><span className="mq-card-name">全部类别</span><Tag style={{ marginInlineEnd: 0 }}>{rows.length} 个</Tag></div>
          <div className="mq-card-count">{totalActive}<small>/ {totalAll} 个版本可用</small></div>
          <div className="mq-card-meta"><span>点击类别卡片只看该类别</span></div>
        </button>
        {rows.map((r) => {
          const latest = versions[r.name]?.find((v) => v.isLatest);
          const module = r.reg?.runtime_module;
          return (
            <button
              type="button" key={r.name} className={`mq-card${name === r.name ? ' is-active' : ''}`}
              onClick={() => setName(name === r.name ? '' : r.name)}
            >
              <div className="mq-card-head">
                <span className="mq-card-name" title={r.name}>{r.name}</span>
                {module
                  ? <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>{RUNTIME_MODULE_LABEL[module] ?? module}</Tag>
                  : <Tag style={{ marginInlineEnd: 0 }}>{r.reg ? '不安装' : '未登记'}</Tag>}
              </div>
              <div className="mq-card-count">{r.active_versions}<small>/ {r.total_versions} 个版本可用</small></div>
              <div className="mq-card-meta">
                <span>最新 <code>{latest?.version ?? '—'}</code></span>
                <span>更新于 {fmtTime(r.last_uploaded)}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mq-panel">
        <div className="mq-filters">
          <Input.Search
            allowClear placeholder="版本号关键字" style={{ width: 220 }}
            value={version} onChange={(e) => setVersion(e.target.value)}
            onSearch={(v, _e, info) => (info?.source === 'clear' ? setVf((p) => ({ ...p, version: '' })) : search(v))}
          />
          <Select
            allowClear showSearch placeholder="上传人" style={{ width: 160 }} value={uploader}
            options={uploaderOptions} onChange={setUploader}
          />
          <DatePicker.RangePicker
            placeholder={['上传开始时间', '上传结束时间']} value={range}
            onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
          />
          <Select
            style={{ width: 120 }} value={status} onChange={setStatus}
            options={[{ label: '可用', value: 'active' }, { label: '已停用', value: 'inactive' }, { label: '全部状态', value: 'all' }]}
          />
          <Select
            style={{ width: 130 }} value={source} onChange={setSource}
            options={[{ label: '全部来源', value: '' }, { label: '手工上传', value: 'upload' }, { label: '中台同步', value: 'ndp' }]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => search()}>查询</Button>
          <Button onClick={resetFilters}>重置</Button>
        </div>
      </div>

      <div className="mq-panel">
        <div className="mq-panel-head">
          <h3>模型类别</h3>
          <span>{versionFiltering ? `命中 ${hitCount} 个版本，分布在 ${filtered.length} 个类别` : `共 ${filtered.length} 个，点击行首 + 查看版本`}</span>
        </div>
        <Table<MergedModelRow>
          rowKey="name" size="middle" loading={loading} dataSource={filtered} columns={columns}
          scroll={{ x: 1400 }} pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
          expandable={{
            expandedRowKeys: expanded,
            onExpandedRowsChange: (keys) => setExpanded(keys as string[]),
            rowExpandable: (r) => r.total_versions > 0,
            expandedRowRender: (r) => (
              <div className="mq-expand">
                <VersionsTable rows={hits[r.name] ?? []} keyword={vf.version ?? ''} onChanged={reload} />
              </div>
            ),
          }}
        />
      </div>

      <RegisterModal
        target={registerTarget} cats={cats} onClose={() => setRegisterTarget(null)}
        onDone={() => { setRegisterTarget(null); reload(); }}
      />
      <EditNameModal
        reg={editTarget} cats={cats} onClose={() => setEditTarget(null)}
        onDone={() => { setEditTarget(null); reload(); }}
      />
    </div>
  );
};

// ============================================================
// 版本子列表：版本号 / 小模型 / 上传人 / 来源 / 操作；版本再展开为小模型列表
// ============================================================

const Highlight: React.FC<{ text: string; kw: string }> = ({ text, kw }) => (
  <>
    {splitHighlight(text, kw).map((p, i) => (p.hit
      ? <mark key={i}>{p.text}</mark>
      : <span key={i}>{p.text}</span>))}
  </>
);

const VersionsTable: React.FC<{ rows: VersionRow[]; keyword: string; onChanged: () => void }> = ({ rows, keyword, onChanged }) => {
  const toggle = async (v: VersionRow) => {
    try {
      await setModelVersionActive(v.name, v.version, !v.active);
      message.success(`${v.name} ${v.version} 已${v.active ? '停用' : '启用'}`);
      onChanged();
    } catch (e) {
      message.error(errText(e));
    }
  };

  const columns: ColumnsType<VersionRow> = [
    {
      title: '版本号', dataIndex: 'version', width: 300,
      render: (v: string, r) => (
        <div className={`mq-ver${r.active ? '' : ' is-off'}`}>
          <span className="mq-ver-dot" />
          <span className="mq-ver-text"><Highlight text={v} kw={keyword} /></span>
          {r.isLatest && <Tag color="success" bordered={false} style={{ marginInlineEnd: 0 }}>最新</Tag>}
          {!r.active && <Tag bordered={false} style={{ marginInlineEnd: 0 }}>已停用</Tag>}
        </div>
      ),
    },
    { title: '小模型', width: 90, render: (_, r) => <SubModelCount files={r.files.map((f) => f.file_name)} /> },
    {
      title: '中台模型名称', width: 240, ellipsis: { showTitle: false },
      render: (_, r) => (r.ndpNames.length
        ? <Tooltip title={r.ndpNames.join('、')}><span>{r.ndpNames.join('、')}</span></Tooltip>
        : <Text type="secondary">—</Text>),
    },
    {
      title: '说明', width: 240, ellipsis: { showTitle: false },
      render: (_, r) => (r.description
        ? <Tooltip title={r.description}><span>{r.description}</span></Tooltip>
        : <Text type="secondary">—</Text>),
    },
    {
      title: '上传人', width: 260,
      render: (_, r) => {
        const [user, domain] = r.uploader.split('@');
        return (
          <Tooltip title={r.uploader}>
            <span className="mq-who">
              <span className="mq-avatar">{user.slice(0, 1).toUpperCase()}</span>
              <span>{user}{domain && <span className="mq-dim">@{domain}</span>}</span>
            </span>
          </Tooltip>
        );
      },
    },
    { title: '上传时间', width: 150, render: (_, r) => <span className="mq-time">{fmtTime(r.time)}</span> },
    {
      title: '来源', dataIndex: 'source', width: 110,
      render: (s: VersionSource) => (
        <Tag bordered={false} color={s === 'ndp' ? 'processing' : 'default'} style={{ marginInlineEnd: 0 }}>{SOURCE_LABEL[s]}</Tag>
      ),
    },
    {
      title: '操作', width: 80, align: 'right',
      render: (_, r) => (
        <Popconfirm
          title={`${r.active ? '停用' : '启用'} ${r.version}？`}
          description="只影响之后新建的主版本；已有主版本与已建 Job 不受影响。"
          okText={r.active ? '停用' : '启用'} cancelText="取消" onConfirm={() => toggle(r)}
        >
          <Button size="small" type="link" danger={r.active}>{r.active ? '停用' : '启用'}</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Table<VersionRow>
      className="mq-vtable" rowKey="version" size="small" dataSource={rows} columns={columns}
      pagination={false}
      locale={{ emptyText: '没有符合筛选条件的版本' }}
      expandable={{
        rowExpandable: (r) => r.files.length > 0,
        expandedRowRender: (r) => <SubModelTable files={r.files.map((f) => f.file_name)} dimmed={!r.active} />,
      }}
    />
  );
};


interface NameFormValues {
  category: string;
  type?: string | null;
  install_dir: string;
  description?: string;
  status?: 'enabled' | 'disabled';
}

const RegisterModal: React.FC<{
  target: UnregisteredModelName | null;
  cats: ModelCategories | null;
  onClose: () => void;
  onDone: (name: string) => void;
}> = ({ target, cats, onClose, onDone }) => {
  const [form] = Form.useForm<NameFormValues>();
  const [saving, setSaving] = useState(false);
  const cat = Form.useWatch('category', form);

  useEffect(() => {
    if (target) {
      form.setFieldsValue({
        category: target.suggested_category ?? undefined,
        type: target.suggested_type ?? undefined,
        install_dir: target.suggested_install_dir ?? '',
        description: '',
      });
    }
  }, [target, form]);

  const submit = async () => {
    if (!target) {
      return;
    }
    const v = await form.validateFields();
    setSaving(true);
    try {
      await createModelName({
        name: target.name,
        category: v.category,
        type: v.type || null,
        install_dir: v.install_dir.trim(),
        description: v.description?.trim() || null,
      });
      message.success(`已登记 ${target.name}`);
      onDone(target.name);
    } catch (e) {
      message.error(`登记失败：${errText(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const conflicts = target ? unregisteredConflicts(target) : [];
  return (
    <Modal
      open={!!target}
      title={`登记模型类别：${target?.name ?? ''}`}
      onCancel={onClose}
      onOk={submit}
      okText="登记"
      confirmLoading={saving}
      destroyOnHidden
    >
      {conflicts.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={conflicts.join('；')} />
      )}
      <NameFields form={form} cats={cats} cat={cat} withStatus={false} />
    </Modal>
  );
};

/** 登记 / 编辑共用的字段：一级分类 → 二级分类 → 安装目录 → 说明（→ 状态）。 */
const NameFields: React.FC<{
  form: ReturnType<typeof Form.useForm<NameFormValues>>[0];
  cats: ModelCategories | null;
  cat: string | undefined;
  withStatus: boolean;
}> = ({ form, cats, cat, withStatus }) => (
  <Form form={form} layout="vertical" preserve={false}>
    <Form.Item name="category" label="一级分类" rules={[{ required: true, message: '请选择一级分类' }]}>
      <Select
        options={Object.entries(cats ?? {}).map(([k, v]) => ({ label: `${v.label} (${k})`, value: k }))}
        onChange={() => form.setFieldValue('type', undefined)}
      />
    </Form.Item>
    <Form.Item name="type" label="二级分类">
      <Select allowClear placeholder="未分类" options={(cats?.[cat ?? '']?.types ?? []).map((t) => ({ label: typeLabelText(cats, t), value: t }))} />
    </Form.Item>
    <Form.Item
      name="install_dir"
      label="安装目录（相对 cyberrt/data）"
      rules={[{ required: true, whitespace: true, message: '请填写安装目录' }]}
    >
      <Input placeholder="如 perception/lidar/models/detection/ut" style={{ fontFamily: 'monospace' }} />
    </Form.Item>
    <Form.Item name="description" label="说明">
      <Input.TextArea rows={2} />
    </Form.Item>
    {withStatus && (
      <Form.Item name="status" label="状态" extra="停用的模型类别不能被新主版本选入">
        <Select options={[{ label: '启用', value: 'enabled' }, { label: '停用', value: 'disabled' }]} />
      </Form.Item>
    )}
  </Form>
);

// ============================================================
// 管理员编辑名称（不能改名）
// ============================================================

const EditNameModal: React.FC<{
  reg: NameReg | null;
  cats: ModelCategories | null;
  onClose: () => void;
  onDone: () => void;
}> = ({ reg, cats, onClose, onDone }) => {
  const [form] = Form.useForm<NameFormValues>();
  const [saving, setSaving] = useState(false);
  const [confirmDir, setConfirmDir] = useState<ModelNamePatch | null>(null);
  const cat = Form.useWatch('category', form);

  useEffect(() => {
    if (reg) {
      form.setFieldsValue({
        category: reg.category,
        type: reg.type ?? undefined,
        install_dir: reg.install_dir,
        description: reg.description ?? '',
        status: reg.status,
      });
    }
  }, [reg, form]);

  const doPatch = async (patch: ModelNamePatch) => {
    if (!reg) {
      return;
    }
    setSaving(true);
    try {
      await patchModelName(reg.name, patch);
      message.success('已保存');
      setConfirmDir(null);
      onDone();
    } catch (e) {
      message.error(`保存失败：${errText(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!reg) {
      return;
    }
    const v = await form.validateFields();
    const patch: ModelNamePatch = {};
    if (v.category !== reg.category) {
      patch.category = v.category;
    }
    const newType = v.type || null;
    if (newType !== reg.type) {
      if (newType) {
        patch.type = newType;
      } else {
        patch.clear_type = true;
      }
    }
    const dir = v.install_dir.trim();
    if (dir !== reg.install_dir) {
      patch.install_dir = dir;
    }
    const desc = v.description?.trim() ?? '';
    if (desc !== (reg.description ?? '')) {
      patch.description = desc;
    }
    if (v.status && v.status !== reg.status) {
      patch.status = v.status;
    }
    if (Object.keys(patch).length === 0) {
      message.info('没有修改');
      return;
    }
    if (patch.install_dir) {
      // 改目录需二次确认（design §6.2）
      setConfirmDir(patch);
      return;
    }
    await doPatch(patch);
  };

  return (
    <>
      <Modal
        open={!!reg}
        title={`编辑模型类别：${reg?.name ?? ''}`}
        onCancel={onClose}
        onOk={submit}
        okText="保存"
        confirmLoading={saving}
        destroyOnHidden
      >
        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
          名称不支持改名（CF 登记、历史主版本都按名称引用）；需要时请新建名称并停用旧名称。
        </div>
        <NameFields form={form} cats={cats} cat={cat} withStatus />
      </Modal>
      <Modal
        open={!!confirmDir}
        title="确认修改安装目录"
        onCancel={() => setConfirmDir(null)}
        onOk={() => confirmDir && doPatch(confirmDir)}
        okText="确认修改"
        confirmLoading={saving}
      >
        <p>
          安装目录 <code>{reg?.install_dir}</code> → <code>{confirmDir?.install_dir}</code>
        </p>
        <p>已有主版本不受影响（清单里已冻结旧目录）；之后新建的主版本按新目录安装。</p>
      </Modal>
    </>
  );
};

export default ModelQuery;
